import React, { useCallback } from 'react';
import { Box } from '@mui/material';
import { colors, Typography, Button } from '@sweep-io/sweep-design';
import { useSelector } from 'react-redux';
import { useIssuesPanelWidth } from '../../hooks/useIssuesPanelWidth';
import { selectIsIssuesPanelOpen } from '../aiAgentCenter/agentsHomeReducer';
import useAgentsHome from '../aiAgentCenter/useAgentsHome';
import useAgentsChats from '../aiAgentCenter/useAgentsChats';
import { selectDefaultCreationCrmOrgId } from '../../reducers/userInfoReducer';
import useObjectTypesWithFetch from '../../hooks/useObjectTypesWithFetch';
import IssuesTableWithFilters from './IssuesTableWithFilters';
import { OnUpdateIssueStatus } from '../aiAgentCenter/aiAgentsConsts';
import { IssueStatus } from '@server/issues';
import useAiAgents from '../aiAgentCenter/useAiAgents';
import { useSweepNotifications } from '../notifications/useSweepNotifications';
import { SweepNotificationVariant } from '../../reducers/notificationsReducer';
import { ResizableInlinePanel } from '../common/panel/ResizableInlinePanel';
import { Issue } from './issuesTypes';
import { resourceTypeToLabel } from './issuesHelper';
import { humanizeDate, humanizeDateVariants } from '../helpers/humanizeDate';
import { downloadCsv } from '../../lib/downloadCsv';

function issuesToCsv(issues: Issue[]): string {
  const headers = ['Name', 'Type', 'Issue', 'Impact', 'Status', 'Last Detected', 'Description'];
  const csvRows = [headers.join(',')];

  for (const issue of issues) {
    const row = [
      issue.resourceName,
      resourceTypeToLabel[issue.resourceType] || issue.resourceType,
      issue.bestPracticeName,
      issue.severity,
      issue.status,
      humanizeDate({
        dateOrTimestamp: issue.createdAt,
        variant: humanizeDateVariants.TWO_DIGITS_WITH_TIME,
      }),
      issue.description,
    ].map((value) => {
      // Escape quotes and wrap in quotes if contains comma, newline, or quote
      const stringValue = String(value ?? '');
      if (stringValue.includes(',') || stringValue.includes('\n') || stringValue.includes('"')) {
        return `"${stringValue.replace(/"/g, '""')}"`;
      }
      return stringValue;
    });
    csvRows.push(row.join(','));
  }

  return csvRows.join('\n');
}

export const IssuesPanel: React.FC = () => {
  const isIssuesPanelOpen = useSelector(selectIsIssuesPanelOpen);
  const { activeAgent } = useAgentsChats();
  const { setReplyText, closeIssuesPanel } = useAgentsHome();
  const crmOrgId = useSelector(selectDefaultCreationCrmOrgId);
  const { objectTypesByName } = useObjectTypesWithFetch({ crmOrgId: crmOrgId ?? '' });
  const { updateIssueStatus } = useAiAgents();
  const { addNotification } = useSweepNotifications();

  const { panelWidth, updatePanelWidth, minWidth, maxWidth } = useIssuesPanelWidth();

  const handleClose = useCallback(() => {
    closeIssuesPanel();
  }, [closeIssuesPanel]);

  const handleUpdateIssueStatus: OnUpdateIssueStatus = useCallback(
    async ({ resourceName, issueId, status }) => {
      if (activeAgent) {
        const updatedSuccessfully = await updateIssueStatus({
          agentId: activeAgent.id,
          issueId,
          newStatus: status as IssueStatus,
        });
        if (updatedSuccessfully) {
          addNotification({
            message: `${resourceName} is marked as ${status}`,
            variant: SweepNotificationVariant.Success,
          });
        }
      }
    },
    [activeAgent, addNotification, updateIssueStatus],
  );

  const handleTextSelection = useCallback(
    (text: string | null) => {
      setReplyText(text);
    },
    [setReplyText],
  );

  const handleDownloadCsv = useCallback(() => {
    if (!activeAgent?.issues?.length) return;

    const csvContent = issuesToCsv(activeAgent.issues);
    const filename = `issues-${activeAgent.name || 'export'}.csv`;
    downloadCsv(csvContent, filename);
  }, [activeAgent]);

  if (!isIssuesPanelOpen || !activeAgent) {
    return null;
  }

  const issues = activeAgent.issues ?? [];
  const objectType =
    'objectType' in activeAgent.contextComponents
      ? activeAgent.contextComponents.objectType
      : undefined;
  const objectLabel = objectType ? (objectTypesByName[objectType]?.label ?? objectType) : '';
  const issuesCount = issues.length;

  const header = (
    <Box
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'flex-start',
        padding: '10px 0 0 16px',
      }}
    >
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
        <Typography variant="h3">Monitoring</Typography>
        <Typography variant="body" color={colors.grey[700]}>
          {issuesCount} Issues Found{objectLabel ? ` in the ${objectLabel} Object` : ''}
        </Typography>
      </Box>
      {issues.length > 0 && (
        <Button startIconName="Download" size="small" variant="system" onClick={handleDownloadCsv}>
          Download
        </Button>
      )}
    </Box>
  );

  return (
    <ResizableInlinePanel
      panelWidth={panelWidth}
      onResize={updatePanelWidth}
      minWidth={minWidth}
      maxWidth={maxWidth}
      onClose={handleClose}
      header={header}
      showHeaderBorder={false}
    >
      <Box
        sx={{
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          overflow: 'hidden',
          padding: '16px',
        }}
      >
        <IssuesTableWithFilters
          onUpdateIssueStatus={handleUpdateIssueStatus}
          issues={issues}
          activeScan={activeAgent.activeScan}
          onTextSelection={handleTextSelection}
        />
      </Box>
    </ResizableInlinePanel>
  );
};

export default IssuesPanel;
